import { type ReactNode, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { LOCALES, T, localeMeta, stripLocale, switchPath, useLocale, useT } from '../i18n'
import { useAuth } from '../lib/auth'
import { setHeroTheme, useHeroTheme } from '../lib/personal'
import { LangNudge } from './LangNudge'

// Always-visible Light | Dark switch for the hero. Two options, one click each.
function ThemeToggle() {
  const theme = useHeroTheme()
  const t = useT()
  return (
    <div
      className="flex items-center rounded-full border border-border p-0.5"
      role="group"
      aria-label={t('nav.theme')}
    >
      <button
        type="button"
        onClick={() => setHeroTheme('light')}
        aria-pressed={theme === 'light'}
        aria-label={t('nav.light')}
        title={t('nav.light')}
        className={`grid h-6 w-6 place-items-center rounded-full transition-colors ${
          theme === 'light' ? 'bg-signal text-on-accent' : 'text-muted hover:text-ink'
        }`}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
        </svg>
      </button>
      <button
        type="button"
        onClick={() => setHeroTheme('dark')}
        aria-pressed={theme === 'dark'}
        aria-label={t('nav.dark')}
        title={t('nav.dark')}
        className={`grid h-6 w-6 place-items-center rounded-full transition-colors ${
          theme === 'dark' ? 'bg-signal text-on-accent' : 'text-muted hover:text-ink'
        }`}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
        </svg>
      </button>
    </div>
  )
}

function AccountLink() {
  const { session } = useAuth()
  const t = useT()
  return (
    <T to="/account" className="hover:text-ink">
      {session ? t('nav.account') : t('nav.signIn')}
    </T>
  )
}

// The brass mark — a rotated square inside a brass tile. Pairs with the "Freight·Rates" wordmark.
function Brand() {
  return (
    <T to="/" className="flex items-center gap-2.5" aria-label="FreightRatesMCP — home">
      <svg width="26" height="26" viewBox="0 0 100 100" aria-hidden="true" className="block shrink-0">
        <circle cx="50" cy="50" r="48" fill="#0c2a5e" /><circle cx="50" cy="50" r="40" fill="#e2231a" /><circle cx="50" cy="50" r="32" fill="#ff7a1f" /><circle cx="50" cy="50" r="24" fill="#ffc42e" /><circle cx="50" cy="50" r="16" fill="#e2231a" /><circle cx="50" cy="50" r="8" fill="#ff7a1f" /><circle cx="50" cy="50" r="3" fill="#fff" />
      </svg>
      <span className="text-[17px] font-semibold tracking-tight text-ink" style={{ fontFamily: "'Ranade', Georgia, serif" }}>
        Freight<span className="text-signal">Rates</span>
      </span>
    </T>
  )
}

// Language picker — links to the same logical page in each locale (keeps the reader on-page across
// languages). Native <details> dropdown, no external deps. Scales as locales are added.
function LocaleSwitcher() {
  const { pathname } = useLocation()
  const active = useLocale()
  const t = useT()
  const current = localeMeta(active)
  return (
    <details className="relative [&_svg.chev]:open:rotate-180">
      <summary
        className="flex cursor-pointer list-none items-center gap-1.5 text-muted hover:text-ink"
        aria-label={t('nav.language')}
      >
        {current.flag}
        <span className="hidden sm:inline">{current.code.toUpperCase()}</span>
        <svg
          className="chev transition-transform"
          width="10"
          height="10"
          viewBox="0 0 12 12"
          aria-hidden="true"
        >
          <path
            d="M2 4l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.6"
            fill="none"
            strokeLinecap="round"
          />
        </svg>
      </summary>
      <ul className="glass absolute right-0 z-50 mt-2 min-w-[150px] rounded-lg border border-border p-1 shadow-lg">
        {LOCALES.map((l) => (
          <li key={l.code}>
            <a
              href={switchPath(l.code, pathname)}
              hrefLang={l.hreflang}
              className={`flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm hover:bg-surface-1 ${
                l.code === active ? 'text-link' : 'text-ink'
              }`}
            >
              {l.flag}
              <span>{l.label}</span>
            </a>
          </li>
        ))}
      </ul>
    </details>
  )
}

export function Layout({ children }: { children: ReactNode }) {
  const t = useT()
  const { pathname } = useLocation()
  const isHome = stripLocale(pathname).replace(/\/$/, '') === ''
  const [scrolled, setScrolled] = useState(false)

  // On the home route the header wears dark frosted glass while it overlaps the deep-ink hero, then
  // snaps to the light frosted bar the moment the dark island's bottom clears the header — precise,
  // not a magic scroll fraction. Measured off the island element HomePage tags `#rt-hero-dark`.
  useEffect(() => {
    if (!isHome) {
      setScrolled(false)
      return
    }
    const onScroll = () => {
      const el = document.getElementById('rt-hero-dark')
      setScrolled(el ? el.getBoundingClientRect().bottom <= 64 : window.scrollY > 400)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [isHome])

  const theme = useHeroTheme()
  const onDark = isHome && !scrolled && theme === 'dark'

  // The Light/Dark switch is a GLOBAL theme: flip the whole document to the deep-ink token values
  // (the same `.tone-dark-vars` the hero island uses) so every page — not just the home hero — obeys it.
  useEffect(() => {
    document.documentElement.classList.toggle('tone-dark-vars', theme === 'dark')
  }, [theme])

  return (
    <div className="flex min-h-screen flex-col bg-void font-sans text-ink">
      <header
        className={`glass sticky top-0 z-40 border-b transition-colors duration-300 ${
          onDark ? 'tone-dark-vars border-transparent' : 'border-border'
        }`}
      >
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
          <Brand />
          <nav className="flex items-center gap-5 text-[13.5px] text-muted">
            <T to="/rate-index" className="hidden hover:text-ink sm:inline">
              {t('nav.rateIndex')}
            </T>
            <T to="/rates" className="hidden hover:text-ink sm:inline">
              {t('nav.lanes')}
            </T>
            <T to="/advertise" className="hidden hover:text-ink md:inline">
              {t('nav.advertise')}
            </T>
            <T to="/pricing" className="hidden hover:text-ink sm:inline">
              {t('nav.pricing')}
            </T>
            <a
              href="https://navo24.com/developers/reference/rates"
              className="hidden hover:text-ink md:inline"
            >
              API
            </a>
            <span className="hidden md:inline">
              <AccountLink />
            </span>
            <Link
              to="/cn"
              className="hidden font-medium text-link hover:opacity-80 lg:inline"
              lang="zh"
            >
              中文 · 货代合作
            </Link>
            <T
              to="/book"
              className="rounded-[9px] bg-signal px-4 py-2 font-semibold text-on-accent shadow-[0_10px_26px_-12px_rgb(var(--signal-rgb)/0.7)] transition-transform hover:-translate-y-px"
            >
              {t('nav.getQuote')}
            </T>
            <ThemeToggle />
            <LocaleSwitcher />
          </nav>
        </div>
      </header>
      <LangNudge />
      <main className="flex-1">{children}</main>
      <footer className="mt-24 border-t border-border">
        <div className="mx-auto grid max-w-6xl gap-8 px-5 py-12 text-sm text-muted md:grid-cols-4">
          <div>
            <div className="text-[15px] font-bold text-ink">
              Freight<span className="text-signal">Rates</span>MCP
            </div>
            <p className="mt-2.5 leading-relaxed">{t('footer.tagline')}</p>
          </div>
          <div>
            <div className="mb-2 font-medium text-ink">{t('footer.product')}</div>
            <ul className="space-y-1.5">
              <li>
                <T to="/rate-index" className="hover:text-ink">
                  {t('footer.rateIndex')}
                </T>
              </li>
              <li>
                <T to="/rates" className="hover:text-ink">
                  {t('footer.laneRates')}
                </T>
              </li>
              <li>
                <T to="/book" className="hover:text-ink">
                  {t('footer.requestQuote')}
                </T>
              </li>
              <li>
                <a href="https://navo24.com/developers/reference/rates" className="hover:text-ink">
                  Developers
                </a>
              </li>
            </ul>
          </div>
          <div>
            <div className="mb-2 font-medium text-ink">{t('footer.partners')}</div>
            <ul className="space-y-1.5">
              <li>
                <T to="/advertise" className="hover:text-ink">
                  {t('footer.advertiseList')}
                </T>
              </li>
              <li>
                <a href="https://schedulesmcp.com" className="hover:text-ink">
                  SchedulesMCP
                </a>
              </li>
              <li>
                <a href="https://trackingmcp.com" className="hover:text-ink">
                  TrackingMCP
                </a>
              </li>
            </ul>
          </div>
          <div>
            <div className="mb-2 font-medium text-ink">{t('footer.legal')}</div>
            <p className="leading-relaxed">{t('footer.legalBody')}</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
